/**
 * Location — the workspace's venue catalog: where an event, or a single
 * ticket type's venue override, takes place.
 *
 * Path: `workspaces/{orgId}/locations/{locationId}` — public read (the
 * buyer needs to know where the event is; the .ics LOCATION field and
 * wallet passes render from here); member create/update behind
 * `tickets.events.manage`, denied until `mfa: true`.
 *
 * `delete` is server-only behind `deleteLocation`: the guard fact spans three
 * references no rule can query — an `Event.locationId` (required, never
 * nullable, so a dangling one has no legal value), a `TicketType.locationId`
 * venue override, and a child venue's `within` — so the door probes all three
 * inside the deleting transaction and refuses with counts. The kernel rule
 * keeps the manage + mfa predicate — a vacuous one would answer
 * `me.can('delete', Location)` true for any authenticated actor — and
 * `.requiresHandler` is what suppresses its emission. The delete reconciler
 * nulling a `within` and a `TicketType.locationId` override that name a
 * removed venue stays as the crash backstop behind that door.
 *
 * Excluded: id — the doc id is the location's own identity, never repeated
 * as a body field; a capacity/room-count field — nothing here manages
 * seating, only where the event is.
 *
 * `timezone` is this document's IANA identifier and the sole place a wall-clock
 * string (an admin form's `datetime-local` value, timezone-less by construction)
 * resolves to a real instant — `wallClockToInstant`/`instantToWallClock` below are
 * the two conversion functions every admin-form save path calls, native `Intl`
 * only. `.default("Europe/Berlin")` means every already-stored document — none of
 * which carries this key — parses unchanged: forward-only, no backfill.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

// A syntactically well-formed IANA identifier — `Intl.DateTimeFormat`'s own
// constructor throws a raw RangeError on garbage input ("Not/AZone"), which
// `offsetMinutesAt`/`wallClockToInstant` below would otherwise propagate
// uncaught. This refine turns that throw into an ordinary zod validation
// failure at the one seam a malformed string could enter through — a
// hand-typed provisioning value, or a future admin-form field — so a typo
// fails loud at parse time, never deep inside a date conversion.
function isIanaTimeZone(tz: string): boolean {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: tz });
		return true;
	} catch {
		return false;
	}
}

export const LocationSchema = z.object({
	name: z.string().min(1),
	address: z
		.string()
		.min(1)
		.describe(
			"the formatted postal address — what the .ics LOCATION field and wallet passes render verbatim, and what the storefront shows as the buyer-facing venue address",
		),
	geo: z
		.object({ lat: z.number(), lng: z.number() })
		.nullish()
		.describe(
			"coordinates for a map pin — absent for a venue with no useful pin (an online-only add-on, an unmapped rural address)",
		),
	url: z
		.string()
		.url()
		.nullish()
		.describe(
			"the venue’s own site or a maps link — a storefront affordance, never required for the artifacts to render",
		),
	notes: z
		.string()
		.nullish()
		.describe(
			"entrance or access hints for the buyer — “stage door, ring the bell” — free text, member-authored",
		),
	within: z
		.string()
		.nullish()
		.describe(
			"the parent venue’s own locationId, when this document is a room inside it (“Halle B” within the venue) — the room composes the parent’s address at render rather than duplicating it. A cross-document pointer zod cannot validate for cycles or dangling targets; the admin form refuses self-reference and cycles, and a dangling pointer fails OPEN — the room renders from its own fields alone",
		),
	timezone: z
		.string()
		.min(1)
		.refine(isIanaTimeZone, { message: "not a valid IANA timezone identifier" })
		.default("Europe/Berlin")
		.describe(
			"the IANA timezone identifier this venue's wall-clock times resolve in — every naive datetime-local value an admin enters for an event or ticket type at this venue (directly, or via TicketType.locationId's override) converts against this zone, never the browser's own. Absent on an already-stored document parses to this deployment's own default rather than a migration — a new fork declares its own value explicitly in its (required, no-default) provisioning config",
		),
});
export type Location = z.infer<typeof LocationSchema>;

// ── The naive wall-clock ⇄ real-instant conversion seam ─────────────────────
// `Intl.DateTimeFormat(..., { timeZone }).formatToParts` is the one JS-native
// primitive that can answer "what does this zone read at this instant" — the
// technique date-fns-tz/luxon build their own zone math on. No dependency: a
// global, not an import, so the .entity role's own allowlist holds.

// The zone's offset (minutes, local-minus-UTC — Europe/Berlin is +60 in CET,
// +120 in CEST) at a given instant, read by asking the zone what wall-clock
// values it renders for that instant and comparing to the instant's own UTC
// reading.
function offsetMinutesAt(timeZone: string, instant: Date): number {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hourCycle: "h23",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}).formatToParts(instant);
	const map: Record<string, string> = {};
	for (const part of parts) map[part.type] = part.value;
	const asIfUTC = Date.UTC(
		Number(map.year),
		Number(map.month) - 1,
		Number(map.day),
		Number(map.hour),
		Number(map.minute),
		Number(map.second),
	);
	return (asIfUTC - instant.getTime()) / 60_000;
}

// The venue wall-clock reading of a real instant — what a `datetime-local`
// control shows, and what every admin-form save path re-derives a Date from.
// No seconds: matches the native control's own `YYYY-MM-DDTHH:mm` value.
export function instantToWallClock(instant: Date, timeZone: string): string {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hourCycle: "h23",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).formatToParts(instant);
	const map: Record<string, string> = {};
	for (const part of parts) map[part.type] = part.value;
	return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}

// The venue wall-clock reading WITH the zone's own UTC offset embedded
// (`2026-08-05T20:00:00+02:00`) — for consumers whose date format requires a
// zone in the value itself (an Apple Wallet pass field renders the embedded
// zone; a `Z` or offset-less value renders the wrong wall time). DST-correct:
// the offset is sampled at this exact instant, never a fixed zone offset.
// `instantToWallClock` above is the offset-less sibling `datetime-local`
// controls and Google Wallet's local `dateTime` consume.
export function instantToOffsetIso(instant: Date, timeZone: string): string {
	const offsetMinutes = offsetMinutesAt(timeZone, instant);
	const sign = offsetMinutes < 0 ? "-" : "+";
	const absolute = Math.abs(offsetMinutes);
	const wallClock = new Date(instant.getTime() + offsetMinutes * 60_000)
		.toISOString()
		.slice(0, 19);
	const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
	const minutes = String(absolute % 60).padStart(2, "0");
	return `${wallClock}${sign}${hours}:${minutes}`;
}

// The real instant a naive `YYYY-MM-DDTHH:mm` wall-clock string means in
// `timeZone` — the one function standing between an admin form's
// timezone-less `datetime-local` value and a true, storable Date.
//
// Reads the naive string as a literal UTC timestamp (a placeholder value with
// no zone meaning of its own), then samples `timeZone`'s offset a day either
// side of it — any DST transition this wall-clock reading could straddle
// falls inside that one-day margin, since no real zone changes offset more
// than once a day. Each distinct sampled offset yields one candidate instant;
// a candidate survives only if re-deriving ITS OWN wall clock in `timeZone`
// reproduces the input exactly — the same round-trip check both the gap and
// the fold policy fall out of directly, with no separate branch for either:
//   - zero survivors → the string names no real instant (spring-forward's
//     nonexistent hour) → `{ error: 'dst-gap' }`, never a silently shifted Date.
//   - one survivor → the ordinary, unambiguous case.
//   - two survivors → fall-back's doubled hour; both are real, so the FIRST
//     occurrence (the earlier UTC instant, pre-transition offset) wins, the
//     java.time/Luxon convention.
export function wallClockToInstant(
	naive: string,
	timeZone: string,
): Date | { error: "dst-gap" } {
	const [datePart, timePart] = naive.split("T");
	const [yearStr, monthStr, dayStr] = datePart?.split("-") ?? [];
	const [hourStr, minuteStr] = timePart?.split(":") ?? [];
	if (
		yearStr === undefined ||
		monthStr === undefined ||
		dayStr === undefined ||
		hourStr === undefined ||
		minuteStr === undefined
	) {
		throw new Error(
			`wallClockToInstant: not a naive "YYYY-MM-DDTHH:mm" string: ${naive}`,
		);
	}
	const asIfUTC = Date.UTC(
		Number(yearStr),
		Number(monthStr) - 1,
		Number(dayStr),
		Number(hourStr),
		Number(minuteStr),
		0,
	);

	const dayMs = 24 * 60 * 60 * 1000;
	const sampledOffsets = new Set([
		offsetMinutesAt(timeZone, new Date(asIfUTC - dayMs)),
		offsetMinutesAt(timeZone, new Date(asIfUTC + dayMs)),
	]);

	const survivors = [...sampledOffsets]
		.map((offset) => asIfUTC - offset * 60_000)
		.filter(
			(candidateMs) =>
				instantToWallClock(new Date(candidateMs), timeZone) === naive,
		)
		.sort((a, b) => a - b);

	const [earliest] = survivors;
	if (earliest === undefined) return { error: "dst-gap" };
	return new Date(earliest);
}

// Every day key from `first` to `last` inclusive, ascending — the candidate
// row an admin form offers where a window is authored as days rather than as
// two full datetimes. Steps the UTC day key itself rather than adding 24h to
// an instant, which repeats or skips a day across a DST boundary.
export function daysBetween(first: string, last: string): readonly string[] {
	const cursor = new Date(`${first}T00:00:00.000Z`);
	const days: string[] = [];
	for (let key = first; key <= last; key = cursor.toISOString().slice(0, 10)) {
		days.push(key);
		cursor.setUTCDate(cursor.getUTCDate() + 1);
	}
	return days;
}

export const Location = entity<Actor>()({
	schema: LocationSchema,
	actions: ["read", "list", "create", "update", "delete"] as const,
})
	.pipe(({ can, when, hasPermission, and }) => [
		// Public: the storefront and every rendered artifact resolve venues
		// with no auth.
		can("read").when(() => and()),
		can("list").when(() => and()),
		// Member CRUD: mirrors Event's manage gate — denied until mfa:true.
		when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		).allow("create", "update"),
		// Same authority, no emitted allow line — see the file-level note.
		can("delete")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.events.manage"),
					me.mfa.eq(true),
				),
			)
			.requiresHandler("deleteLocation"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/locations/{locationId}",
		}),
	);
